import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Shield, GraduationCap, MapPin, Landmark, MessageSquare, BadgeCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { EventList } from "../components/EventList";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityPage() {
  const { cityName } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    axios.get(`${API}/cities/${encodeURIComponent(cityName)}`).then((r) => {
      setData(r.data);
      axios.post(`${API}/track`, { type: "City", name: r.data.city, link: `/cities/${encodeURIComponent(r.data.city)}` }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [cityName]);

  if (notFound) return <div className="py-24 text-center text-slate-500">City not found. <Link to="/countries" className="text-[#0055FF] underline">Back to Countries</Link></div>;
  if (!data) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  return (
    <div>
      <PageHero topic={`${data.city} ${data.country} city sports`} title={`${data.flag} ${data.city}`} subtitle={`The living sports page for ${data.city}, ${data.state}, ${data.country} — clubs, venues, schools, events and community life.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <Link to="/countries" data-testid="back-to-countries" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All Countries & Places</T></Link>

        <div className="flex flex-wrap items-center gap-2" data-testid="city-breadcrumb">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{data.continent}</span>
          <span className="text-slate-300">›</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{data.country}</span>
          <span className="text-slate-300">›</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{data.state}</span>
          <span className="text-slate-300">›</span>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#0055FF]">{data.city}</span>
        </div>

        <AIContent topic={`Sports life in ${data.city}, ${data.country}`} context={`Editorial about the sporting identity of ${data.city}, ${data.state}, ${data.country}. Popular national sports: ${data.top_sports.join(", ")}. Cover local sporting culture, participation opportunities, venues and community spirit. Do not invent specific clubs, results or statistics.`} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="city-teams">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Shield className="h-5 w-5 text-[#0055FF]" /> <T>Clubs & Teams</T></h2>
            {data.teams.length ? (
              <div className="mt-4 space-y-3">
                {data.teams.map((t) => (
                  <Link key={t.id} to={`/teams/${t.id}`} data-testid={`city-team-${t.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 p-4 transition-all hover:border-[#0055FF]">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.sport} · {t.league}</p>
                    </div>
                    <span className="text-xs font-bold text-[#0055FF]">→</span>
                  </Link>
                ))}
              </div>
            ) : <p className="mt-4 text-sm text-slate-500"><T>No verified clubs listed yet for this city in the demo dataset.</T></p>}
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="city-venues">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Landmark className="h-5 w-5 text-[#0055FF]" /> <T>Venues & Facilities</T></h2>
              {data.venues.length ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.venues.map((v) => <span key={v} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700"><MapPin className="h-3.5 w-3.5 text-[#0055FF]" />{v}</span>)}
                </div>
              ) : <p className="mt-4 text-sm text-slate-500"><T>No venues listed yet in the demo dataset.</T></p>}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="city-schools">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><GraduationCap className="h-5 w-5 text-[#0055FF]" /> <T>Schools & Academies</T></h2>
              {data.schools.length ? (
                <div className="mt-4 space-y-3">
                  {data.schools.map((s) => (
                    <Link key={s.id} to={`/schools/${s.id}`} data-testid={`city-school-${s.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 p-4 transition-all hover:border-[#0055FF]">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{s.name}</p>
                        <p className="text-xs text-slate-500"><T>{s.type}</T> · {s.sports_offered.slice(0, 3).join(", ")}</p>
                      </div>
                      <span className="text-xs font-bold text-[#0055FF]">→</span>
                    </Link>
                  ))}
                </div>
              ) : <p className="mt-4 text-sm text-slate-500"><T>No schools listed yet in the demo dataset.</T></p>}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="city-events">
          <h2 className="mb-4 text-lg font-bold text-slate-900"><T>Events in this City</T></h2>
          <EventList events={data.events} />
          <p className="mt-3 text-[11px] italic text-slate-400"><T>Curated demo data — clearly labeled, not a live feed.</T></p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="city-community">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><MessageSquare className="h-5 w-5 text-[#0055FF]" /> <T>Community Posts</T></h2>
            <Link to="/community" data-testid="city-to-community" className="rounded-full bg-[#0055FF] px-4 py-2 text-xs font-bold text-white hover:bg-[#0040CC]"><T>Join the conversation</T></Link>
          </div>
          {data.posts.length ? (
            <div className="mt-4 space-y-3">
              {data.posts.map((p) => (
                <div key={p.post_id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-[#0055FF]">{p.category}</span>
                    {p.label === "verified-org" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3" /><T>Verified org</T></span>}
                  </div>
                  <p className="mt-2 text-sm font-bold text-slate-900">{p.title}</p>
                  <p className="mt-1 text-sm text-slate-600">{p.body}</p>
                  <p className="mt-2 text-[11px] text-slate-400">{p.author_name}</p>
                </div>
              ))}
            </div>
          ) : <p className="mt-4 text-sm text-slate-500"><T>No community posts mention this city yet — be the first!</T></p>}
        </div>
      </div>
    </div>
  );
}
